import ReactDOM from 'react-dom';
import React, { useEffect, useState } from 'react';
import { InferGetStaticPropsType, GetStaticProps, GetStaticPaths } from 'next';
import { NextSeo, ProductJsonLd, FAQPageJsonLd } from 'next-seo';
import Link from 'next/link';
import Breadcrumb from 'src/components/Breadcrumb';
import BreadcrumbItem from 'src/components/BreadcrumbItem';
import { AppRoutes } from 'src/lib/routes';
import dayjs from 'dayjs';
import { commonCarouselSettings, DEFAULT_PAGINATION, UIDateFormat } from 'src/lib/constants';
import { displayReadTime, getEleOffset, humanizeMoney } from 'src/helpers';
import { Blog, ExternalBlog, EnumPriceUnit, Collection, Option, SiteReview, EnumOptionAccommodationSaleType, TMoney, FaqItem, Pagination } from 'src/types';
import ApiClient from 'src/lib/api_client';
import BlogStyleArticle from 'src/components/BlogStyleArticle';
import BlogStyleAlist from 'src/components/BlogStyleAlist';
import BlogStyleList from 'src/components/BlogStyleList';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import ImageWithFallback from 'src/components/ImageWithFallback';
import { sortBy, flatten, round, truncate } from 'lodash-es';
import RevalidateSettings from 'src/lib/revalidate_settings';
import useCurrencyExchange from 'src/hooks/useCurrencyExchange';
import useCurrentCurrency from 'src/hooks/useCurrentCurrency';
import CollectionCard from 'src/components/blog/CollectionCard';
import OptionCard from 'src/components/blog/OptionCard';
import VideoCard from 'src/components/VideoCard';
import humps from 'humps';
import videoCardStyle from 'src/styles/modules/components/video_card.module.scss';
import BlogCardV2 from 'src/components/BlogCardV2';
import CommunityInstagramV2 from 'src/components/CommunityInstagramV2';
import { nanoid } from 'nanoid';
import MobileSummaryButton, {
  SummaryItem,
  SummaryList,
} from 'src/components/blog/MobileSummaryButton';
import BlogEmbedCard from 'src/components/blog/BlogEmbedCard';
import Player from '@vimeo/player';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import Error404Page from 'src/pages/404.js';
import useCurrentUser from 'src/hooks/useCurrentUser';
import Slider from 'react-slick';
import classNames from 'classnames';
import AdminBar from 'src/components/AdminBar';
import { AdminRoutes } from 'src/lib/routes';
import { canShowAverageAdvancedGroupPrice } from 'src/helpers';
import UserFaqSection from 'src/components/UserFaqSection';
import Banner from 'src/components/Banner';
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from 'react-icons/hi';
import useBreakpoints from 'src/hooks/useBreakpoints';
import SliderOrDiv from 'src/components/SliderOrDiv';

const PlayersContext = React.createContext<{
  players: Player[];
  setPlayers: React.Dispatch<React.SetStateAction<Player[]>>;
}>({
  players: [],
  setPlayers: () => [],
});

const PlayersProvider: React.ReactFCWithChildren = ({ children }) => {
  const [players, setPlayers] = useState<Player[]>([]);

  return (
    <PlayersContext.Provider value={{ players, setPlayers }}>
      {children}
    </PlayersContext.Provider>
  );
};

const BlogVideoEmbed: React.FC<{ id: number | string }> = ({ id }) => {
  const { players, setPlayers } = React.useContext(PlayersContext);

  return (
    <div className="video tw-my-8 md:tw-my-10">
      <div
        className={`${videoCardStyle.videoContainer} tw-w-full md:tw-w-96 tw-mx-auto tw-border-2 tw-border-brand-green tw-rounded-lg`}
        onClick={() => {
          players.forEach((player) => {
            player.getVideoId().then((videoId) => {
              if (videoId !== Number(id)) {
                player.pause();
              }
            });
          });
        }}
      >
        <VideoCard vimeoId={id} unmuteOnPlay={true} setPlayers={setPlayers} />
      </div>
    </div>
  );
};

const Page: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = ({
  blog,
  linkableFaqsData,
  relatedBlogs,
  externalBlog,
  siteReview,
}) => {
  const { isScreenMd } = useBreakpoints();
  const [currentUser] = useCurrentUser()
  const increaseViewCount = useEnhanceApiClient(ApiClient.increaseViewCount);
  const getOptions = useEnhanceApiClient(ApiClient.getOptions);
  const currentCurrency = useCurrentCurrency();
  const currencyExchange = useCurrencyExchange();
  const pageUrl = AppRoutes.shortMagazineRoute({ id: blog.slug }).toUrl();
  const [h2Children, setH2Children] = useState<{ [key: string]: SummaryItem[] }>({});
  const [h2Eles, setH2Eles] = useState<SummaryItem[]>([]);
  const canView = (blog.isPublished || currentUser?.isAdmin)
  const { data: linkableFaqs, pagination: faqPagination } = linkableFaqsData || {
    data: [],
    pagination: DEFAULT_PAGINATION,
  };
  const showLinkableFaqs = blog.linkableType && ['City', 'Collection'].includes(blog.linkableType)

  useEffect(() => {
    if (['list', 'alist'].includes(externalBlog.type)) {
      document.querySelectorAll('.card-oembed__subtitle').forEach((ele) => {
        ele.classList.add('d-none');
      });
    }

    if (typeof window === 'object') {
      document.querySelectorAll('.js-blog-body .block-img').forEach((ele) => {
        if (ele instanceof HTMLElement) {
          const caption = ele.querySelector('img')?.getAttribute('alt');
          if (!caption) return;

          let p = document.createElement('p');
          p.className = 'block-img__caption';
          p.textContent = caption;
          ele.appendChild(p);
        }
      });

      document.querySelectorAll('.js-img-option-oembed').forEach((ele) => {
        if (ele instanceof HTMLElement) {
          const container = ele.closest('.js-oembed-container');
          if (container instanceof HTMLElement) {
            var imgSrc = container.dataset.img;
            if (imgSrc && imgSrc.length > 0) {
              ele.setAttribute('src', imgSrc);
            }
          }
        }
      });

      document.querySelectorAll('.js-oembed-container').forEach((ele) => {
        if (!(ele instanceof HTMLElement)) return;

        var price = ele.querySelector('.js-option-embed-price');
        if (!(price instanceof HTMLElement)) return;
        var optionId = price.dataset.id;
        var btnBookNow = ele.parentElement?.querySelector('.js-oembed-btn-book-now') as
          | HTMLElement
          | undefined;
        var link = ele.querySelector('.card-oembed')?.parentElement as HTMLElement | undefined;

        if (btnBookNow) {
          if (optionId) {
            btnBookNow.setAttribute(
              'href',
              link
                ? link.getAttribute('href') || '#'
                : AppRoutes.experienceRoute({ id: optionId }).toPath()
            );
          } else {
            btnBookNow.remove();
          }
        }

        if (optionId && link && process.env.NEXT_PUBLIC_APP_ENV === 'production') {
          link.addEventListener('click', function () {
            window.gtag('event', 'magazine_option_link_click', {
              event_category: 'link_click',
              event_label: `Magazine#${blog.id} - Option#${optionId}`,
            });
          });
        }
      });

      document.querySelectorAll('.js-blog-collection').forEach((ele) => {
        if (ele instanceof HTMLDivElement) {
          const props = humps.camelizeKeys(
            JSON.parse(ele.dataset.reactProps || '{}')
          ) as Collection;
          ReactDOM.render(<CollectionCard collection={props} />, ele);
        }
      });

      document.querySelectorAll('.js-blog-blog').forEach((ele) => {
        if (ele instanceof HTMLDivElement) {
          const props = humps.camelizeKeys(JSON.parse(ele.dataset.reactProps || '{}')) as Blog;
          ReactDOM.render(<BlogEmbedCard blog={props} />, ele);
        }
      });

      let fetchedOptions: Option[] = [];
      const optionIds = Array.from(document.querySelectorAll('.js-blog-option'))
        .map((ele) => {
          if (ele instanceof HTMLDivElement) {
            const props = humps.camelizeKeys(JSON.parse(ele.dataset.reactProps || '{}')) as Option;
            return Number(props.id)
          }
        })
        .filter((o) => typeof o === 'number') as number[];

      if (optionIds.length > 0) {
        getOptions({ q: { id_in: optionIds }, per_page: optionIds.length })
          .then((result) => {
            const [data] = result;

            if (!('error' in data)) {
              fetchedOptions = data.data;
            }
          })
          .finally(() => {
            document.querySelectorAll('.js-blog-option').forEach((ele) => {
              if (ele instanceof HTMLDivElement) {
                const props = humps.camelizeKeys(
                  JSON.parse(ele.dataset.reactProps || '{}')
                ) as Option;
                const option = fetchedOptions.find((o) => o.id === Number(props.id));
                ReactDOM.render(
                  <OptionCard
                    option={{
                      ...props,
                      ...(option ? { name: option.name, minPrice: option.minPrice } : {}),
                    }}
                  />,
                  ele
                );
              }
            });
          });
      }

      document.querySelectorAll('.js-blog-video').forEach((ele) => {
        if (ele instanceof HTMLDivElement) {
          const props = humps.camelizeKeys(JSON.parse(ele.dataset.reactProps || '{}')) as {
            id: number | string;
            width?: number;
            height?: number;
          };
          ReactDOM.render(
            <PlayersProvider>
              <BlogVideoEmbed id={props.id} />
            </PlayersProvider>,
            ele
          );
        }
      });

      const h2s = Array.from(document.querySelectorAll('.js-blog-body h2')).map((ele, index) => {
        return {
          id: nanoid(),
          ele: ele as HTMLElement,
          tag: 'h2',
          position: index,
          active: false,
        };
      });
      setH2Eles(h2s);
      const h3Eles = Array.from(document.querySelectorAll('.js-blog-body h3')).map((ele, index) => {
        return {
          id: nanoid(),
          ele: ele as HTMLElement,
          tag: 'h3',
          position: index,
          active: false,
        };
      });
      const childEles = sortBy(h2s, (o) => -o.position).reduce((memo, h2) => {
        const includedH3Eles = flatten(Object.values(memo));
        memo[h2.id] = h3Eles.filter((o) => {
          const notIncluded = !includedH3Eles.find((v) => v.id === o.id);
          const position = h2.ele.compareDocumentPosition(o.ele);
          const isAfterH2 = position & 0x04 ? true : false;
          return notIncluded && isAfterH2;
        });
        return memo;
      }, {} as { [key: string]: typeof h3Eles });
      setH2Children(childEles);

      const summaryItems = flatten(
        h2s.map((h2) => {
          return [h2, ...childEles[h2.id]];
        })
      ).filter((v) => v) as SummaryItem[];

      document.addEventListener('scroll', () => {
        const currentPosition = window.scrollY;
        let nearestSummaryItem: SummaryItem | undefined;

        summaryItems.forEach((summaryItem) => {
          if (currentPosition > getEleOffset(summaryItem.ele).top - 120) {
            nearestSummaryItem = summaryItem;
          }
        });

        if (nearestSummaryItem) {
          if (nearestSummaryItem.tag === 'h2') {
            setH2Eles((prev) =>
              prev.map((item) => {
                return {
                  ...item,
                  active: item.id === nearestSummaryItem?.id,
                };
              })
            );
            setH2Children((prev) =>
              Object.keys(prev).reduce((memo, id) => {
                memo[id] = prev[id].map((h3) => {
                  return {
                    ...h3,
                    active: false,
                  };
                });
                return memo;
              }, {} as { [key: string]: typeof h3Eles })
            );
          } else if (nearestSummaryItem.tag === 'h3') {
            setH2Eles((prev) =>
              prev.map((item) => {
                return {
                  ...item,
                  active: false,
                };
              })
            );
            setH2Children((prev) =>
              Object.keys(prev).reduce((memo, id) => {
                memo[id] = prev[id].map((h3) => {
                  return {
                    ...h3,
                    active: h3.id === nearestSummaryItem?.id,
                  };
                });
                return memo;
              }, {} as { [key: string]: typeof h3Eles })
            );
          }
        } else {
          setH2Eles((prev) =>
            prev.map((item) => {
              return {
                ...item,
                active: false,
              };
            })
          );
          setH2Children((prev) =>
            Object.keys(prev).reduce((memo, id) => {
              memo[id] = prev[id].map((h3) => {
                return {
                  ...h3,
                  active: false,
                };
              });
              return memo;
            }, {} as { [key: string]: typeof h3Eles })
          );
        }
      });
    }
  }, [blog.id, canView]);

  useEffect(() => {
    if (blog.optionPrice) {
      const optionPrice = blog.optionPrice;
      document.querySelectorAll('.js-option-embed-price').forEach((ele) => {
        if (ele instanceof HTMLElement) {
          var optionId = ele.dataset.id;
          var price = optionPrice[Number(optionId)];
          if (price) {
            const shouldShowDisplayAdvancedPrice =
              canShowAverageAdvancedGroupPrice(price) && price.displayAdvancedGroupPrice;
            const minPrice =
              price.accommodationSaleType === EnumOptionAccommodationSaleType.pickRoom &&
              price.priceUnit === EnumPriceUnit.perRoom &&
              price.roomDisplayPrice
                ? price.roomDisplayPrice.price as TMoney
                : shouldShowDisplayAdvancedPrice
                ? price.displayAdvancedGroupPrice?.price as TMoney
                : price.amount
            const priceUnit = shouldShowDisplayAdvancedPrice
              ? '/ person'
              : price.priceUnit === EnumPriceUnit.perGroup
              ? price.yachtTrip
                ? '/ yacht'
                : '/ group'
              : '/ person'

            if (minPrice) {
              ele.textContent = `From ${humanizeMoney(
                currencyExchange({ amount: {
                  amount: round(Number(minPrice.amount), 0),
                  currency: minPrice.currency
                }, toCurrency: currentCurrency })
              )} ${priceUnit}`;
            }
          } else {
            ele.closest('.card-oembed')?.classList?.add('card-oembed--disabled');
            ele.closest('a')?.addEventListener('click', (e: MouseEvent) => {
              e.preventDefault();
            });
          }
        }
      });
    }
  }, [blog.id, currentCurrency, currencyExchange, canView]);

  useEffect(() => {
    if (blog.id) {
      increaseViewCount({ view_countable_id: blog.id, view_countable_type: 'Blog' });
    }
  }, [blog.id]);

  return canView ? (
    <>
      <NextSeo
        title={blog.title}
        description={blog.metaDescription}
        canonical={pageUrl}
        openGraph={{
          url: pageUrl,
          title: blog.title,
          description: blog.metaDescription,
          images: blog.coverImgUrl
            ? [
                {
                  url: blog.coverImgUrl,
                  width: 600,
                  height: 600,
                  alt: blog.title,
                },
              ]
            : [],
        }}
      />

      <WithDefaultJsonLd>
        <ProductJsonLd
          productName={blog.title}
          url={pageUrl}
          description={blog.metaDescription}
          images={blog.coverImgUrl ? [blog.coverImgUrl] : []}
          aggregateRating={{
            ratingValue: siteReview.rating,
            reviewCount: siteReview.count,
          }}
        />
        {blog.nextSeoFaqs && blog.nextSeoFaqs.length > 0 ? (
          <FAQPageJsonLd mainEntity={blog.nextSeoFaqs} />
        ) : null}
      </WithDefaultJsonLd>

      <AdminBar updatedAt={blog.updatedAt}>
        <>
          <a
            href={AdminRoutes.adminEditBlogRoute({ id: blog.id }).toUrl()}
            className="btn btn-primary mb-2"
          >
            Edit Blog
          </a>
          <a
            href={AdminRoutes.adminBlogsRoute({}).toUrl()}
            className="btn btn-outline-primary mb-2"
          >
            Blog Dashboard
          </a>
        </>
      </AdminBar>

      <div className="tw-bg-brand-yellow">
        <div className="container">
          <div className="tw-py-6 md:tw-py-10">
            <Breadcrumb className="md:tw-justify-center">
              <BreadcrumbItem>
                <Link legacyBehavior prefetch={false} href={AppRoutes.magazinesRoute({}).toPath()}>Field notes</Link>
              </BreadcrumbItem>
              {blog.country && (
                <>
                  <BreadcrumbItem>
                    <Link legacyBehavior prefetch={false} href={AppRoutes.magazineCateRoute({ cate: blog.country.slug }).toPath()}>
                      {blog.country.name}
                    </Link>
                  </BreadcrumbItem>
                </>
              )}
            </Breadcrumb>
          </div>

          {h2Eles.length > 0 ? (
            <MobileSummaryButton key={blog.id} h2Eles={h2Eles} h2Children={h2Children} blog={blog} blogType={externalBlog.type} />
          ) : null}

          <div className="blog">
            <div className="tw-mx-auto tw-max-w-3xl">
              <h1 className="tw-text-4xl md:tw-text-8xl tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none tw-mb-4 md:tw-mb-6 tw-text-center">
                {blog.title}
              </h1>
              <h2 className="tw-font-styleSans tw-font-semibold tw-text-xl md:tw-text-2xl tw-mb-4 md:tw-mb-6 tw-leading-none md:tw-leading-none tw-text-center">{blog.excerpt}</h2>
            </div>
            <div className="tw-mt-4 md:tw-mt-6 tw-text-center tw-mb-8 md:tw-mb-16">
              <div className="">
                <span className="tw-text-sm md:tw-text-base">
                  Last updated: {dayjs(blog.updatedAt).format(UIDateFormat)}
                </span>{' '}
                <span className="tw-text-sm md:tw-text-base">
                  {displayReadTime(blog.actualReadTime)}
                </span>
              </div>
            </div>
            {blog.coverImgUrl && (
              <div className="tw--mx-3.75 md:tw-mx-0">
                <ImageWithFallback
                  src={blog.coverImgUrl}
                  className="tw-object-cover tw-block tw-static tw-w-full tw-h-96 md:tw-h-full md:tw-rounded-2xl md:tw-max-h-md tw-mb-10 md:tw-mb-16 md:tw-border-2 tw-border-solid tw-border-brand-green"
                  alt={blog.title}
                  fill
                  unoptimized={true}
                />
              </div>
            )}

            <div className="row tw-pb-16 md:tw-pb-20">
              <div className="col-md-8">
                {externalBlog.type === 'alist' ? (
                  <BlogStyleAlist externalBlog={externalBlog} />
                ) : externalBlog.type === 'list' ? (
                  <BlogStyleList externalBlog={externalBlog} />
                ) : externalBlog.type === 'article' ? (
                  <BlogStyleArticle externalBlog={externalBlog} />
                ) : (
                  ''
                )}
              </div>
              <div className="tw-hidden md:tw-block col-md-4">
                {relatedBlogs.length > 0 ? (
                  <div className="tw-bg-white tw-border tw-border-brand-green tw-p-6 tw-rounded-lg">
                    <div className="tw-font-styleSans tw-font-semibold tw-text-2xl tw-leading-none tw-mb-4">
                      Try these next
                    </div>

                    {relatedBlogs.slice(0, 4).map((blog, index) => (
                      <Link legacyBehavior prefetch={false}                         key={blog.id}
                        href={AppRoutes.shortMagazineRoute({ id: blog.slug }).toPath()}
                      >
                        <a
                          className={`tw-flex tw-items-center ${
                            index > 0 ? 'tw-border-t tw-border-grey-lighter tw-pt-4 tw-mt-4' : ''
                          }`}
                        >
                          <ImageWithFallback
                            src={blog.coverImgUrl}
                            alt=""
                            fill
                            unoptimized={true}
                            className="tw-rounded-lg tw-h-24 tw-w-24 tw-block tw-static tw-object-cover tw-mr-3 tw-flex-shrink-0"
                          />
                          <div className="tw-text-brand-navy tw-font-styleSans tw-font-semibold">{blog.title}</div>
                        </a>
                      </Link>
                    ))}
                  </div>
                ) : null}

                {h2Eles.length > 0 ? (
                  <div className={classNames('tw-bg-white tw-border tw-border-brand-green tw-p-6 tw-rounded-lg tw-sticky tw-top-32', { 'tw-mt-16': relatedBlogs.length > 0 })}>
                    <SummaryList
                      blog={blog}
                      key={blog.id}
                      h2Eles={h2Eles}
                      blogType={externalBlog.type}
                      h2Children={h2Children}
                      containerClass="tw-max-h-sm tw-overflow-y-auto tw-overflow-x-hidden"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showLinkableFaqs ? (
        linkableFaqs.length > 0 ? (
          <div className="tw-bg-grey-lightest tw-py-16 md:tw-py-24">
            <div className="container md:tw-max-w-3xl">
              <UserFaqSection
                faqs={linkableFaqs}
                title={`${blog.linkable?.name} FAQs`}
                limit={5}
                hiddenAskQuestion={true}
                faqPagination={faqPagination}
                faqable={{
                  id: String(blog.linkableId),
                  type: blog.linkableType as 'City' | 'Collection',
                }}
                isAdmin={currentUser?.isAdmin || false}
              />
            </div>
          </div>
        ) : null
      ) : blog.faqs && blog.faqs.length > 0 ? (
        <div className="tw-bg-grey-lightest tw-py-16 md:tw-py-24">
          <div className="container md:tw-max-w-3xl">
            <UserFaqSection
              faqs={blog.faqs}
              title={'Frequently Asked Questions'}
              limit={5}
              hiddenAskQuestion={true}
              faqPagination={faqPagination}
              faqable={{ id: String(blog.id), type: 'Blog' }}
              isAdmin={currentUser?.isAdmin || false}
            />
          </div>
        </div>
      ) : null}

      {blog.linkable && blog.linkableType ? (
        <Banner
          title={
            blog.linkableType === 'Collection'
              ? `${blog.linkable.kind === 'list' ? '' : `${blog.linkable.optionsCount} Best `}${
                  blog.linkable.name
                }`
              : `Top things to do in ${blog.linkable.name}`
          }
          image={blog.linkable.image}
          url={
            blog.linkableType === 'Collection'
              ? AppRoutes.collectionRoute({ id: String(blog.linkable.slug) }).toPath()
              : blog.linkableType === 'City'
              ? AppRoutes.cityRoute({ id: blog.linkable.slug }).toUrl()
              : blog.linkable.slug === 'singapore'
              ? AppRoutes.cityRoute({ id: blog.linkable.slug }).toUrl()
              : AppRoutes.countryRoute({ id: blog.linkable.slug }).toUrl()
          }
          urlLabel={`explore ${blog.linkableType === 'Collection' ? 'all' : blog.linkable.name}`}
        />
      ) : null}

      {relatedBlogs.length > 0 && (
        <div className="tw-bg-white tw-py-16 md:tw-py-24">
          <div className="container tw-px-4">
            <div className="md:tw-py-4 tw-pb-7 md:tw-border-t md:tw-border-b tw-border-grey-lighter md:tw-mb-8">
              <h2 className="tw-font-styleSans tw-font-semibold tw-leading-none md:tw-leading-none tw-text-center tw-text-2xl md:tw-text-4xl tw-mb-0">
                More Fieldnotes
              </h2>
            </div>
            <SliderOrDiv
              {...commonCarouselSettings}
              slidesToShow={3}
              isSlider={isScreenMd}
              slidesToScroll={1}
              className={
                isScreenMd ? 'tw--mx-2 md:tw--mx-4 slider-v2 carousel-field-notes' : 'tw-px-2'
              }
              dots={false}
              responsive={[
                {
                  breakpoint: 800,
                  settings: {
                    slidesToShow: 1,
                    arrows: false,
                  },
                },
              ]}
              prevArrow={
                <div className="slick-prev">
                  <HiOutlineArrowNarrowLeft
                    size={24}
                    strokeWidth={2}
                    className="tw-text-brand-navy"
                  />
                </div>
              }
              nextArrow={
                <div className="slick-next">
                  <HiOutlineArrowNarrowRight
                    size={24}
                    strokeWidth={2}
                    className="tw-text-brand-navy"
                  />
                </div>
              }
            >
              {relatedBlogs.slice(0, isScreenMd ? 9 : 5).map((blog) => (
                <>
                  <BlogCardV2 key={blog.id} blog={blog} className="tw-px-4 md:tw-block tw-hidden" />
                  <div className="tw-flex tw-mb-4 tw-block md:tw-hidden">
                    <div className="md:tw-mr-8 tw-mr-4 tw-outline-none">
                      <ImageWithFallback
                        src={blog.coverImgUrl}
                        alt={blog.title}
                        fill
                        className="tw-static tw-object-cover md:tw-w-60 md:tw-h-40 tw-w-40 tw-h-32 tw-border-2 tw-border-brand-green tw-border-solid tw-rounded-lg"
                        unoptimized={true}
                      />
                    </div>
                    <div className="tw-flex tw-flex-col tw-justify-center">
                      <Link
                        legacyBehavior prefetch={false}
                        href={AppRoutes.shortMagazineRoute({ id: blog.slug }).toPath()}
                      >
                        <a className="tw-text-lg tw-font-styleSans tw-font-semibold tw-text-brand-navy tw-underline">
                          {truncate(blog.title, { length: 50, omission: '...' })}
                        </a>
                      </Link>
                    </div>
                  </div>
                </>
              ))}
            </SliderOrDiv>
          </div>
        </div>
      )}
    </>
  ) : (
    <Error404Page />
  );
};

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<{
  blog: Blog;
  linkableFaqsData: {
    data: FaqItem[],
    pagination: Pagination,
  }
  externalBlog: ExternalBlog;
  relatedBlogs: Blog[];
  siteReview: SiteReview;
}> = async (context) => {
  const { id } = context.params || {};

  if (!id) {
    return {
      notFound: true,
    };
  }

  const [blog] = await ApiClient.getBlog({ id: String(id), view: 'with_external_blog' });

  if ('error' in blog || !blog.externalBlog) {
    return {
      notFound: true,
    };
  }

  if (blog.redirectUrl) {
    return {
      redirect: {
        destination: blog.redirectUrl,
        statusCode: 301,
      },
    };
  }

  const [{ data: cityBlogs }] = blog.city
    ? await ApiClient.getBlogs({
        excluded_ids: [blog.id],
        city_ids: [blog.city.id],
        sort_by: ['most_read', 'latest'],
        page: 1,
        per_page: 3,
      })
    : [{ data: [] as Blog[] }];

  const [{ data: countryBlogs }] = blog.country
    ? await ApiClient.getBlogs({
        sort_by: ['most_read', 'latest'],
        excluded_ids: cityBlogs.map((v) => v.id).concat([blog.id]),
        country_id: blog.country.id,
        page: 1,
        per_page: 3,
      })
    : [{ data: [] as Blog[] }];

  const [{ data: linkableFaqs, pagination: faqPagination }] =
    blog.linkableType && blog.linkableId && ['City', 'Collection'].includes(blog.linkableType)
      ? await ApiClient.getFaqs({
          faqable_type: blog.linkableType,
          faqable_id: blog.linkableId,
          per_page: 5,
        })
      : [{ data: [] as FaqItem[], pagination: DEFAULT_PAGINATION }];

  const relatedBlogs = cityBlogs.concat(countryBlogs).slice(0, 8);
  const [siteReview] = await ApiClient.getSiteReviews();

  return {
    props: {
      blog: blog,
      linkableFaqsData: {
        data: linkableFaqs,
        pagination: faqPagination
      },
      externalBlog: blog.externalBlog,
      relatedBlogs: relatedBlogs,
      siteReview: siteReview,
    },
    revalidate: RevalidateSettings.short,
  };
};

export default Page;
